import React, { useState, useCallback } from 'react';
import { Project } from '../../types';
import { useEnablement } from './hooks/useEnablement';
import { useAzureOpenAI } from './hooks/useAzureOpenAI';
import { EmptyState } from './components/EmptyState';
import { Editor } from './components/Editor';
import { MockNotification } from './components/MockNotification';
import { getApiBaseUrl } from '../../lib/api';

interface EnablementTabProps {
  project: Project;
}

export const EnablementTab: React.FC<EnablementTabProps> = ({ project }) => {
  const [showEditor, setShowEditor] = useState(false);
  const { enablement, loading, saving, lastSaved, autoSave, publishEnablement, createEnablement } = useEnablement({ projectId: project.id });
  const { generateInstructions, isGenerating, useMock } = useAzureOpenAI();
  const API_URL = getApiBaseUrl();

  const handleGenerate = useCallback(async () => {
    try {
      // Ensure we always have the latest spec. If missing, fetch the project details.
      let specToUse: string | null = project.specContent ?? null;

      if (!specToUse) {
        try {
          const { supabase } = await import('../../lib/supabase');
          const { data: { session } } = await supabase.auth.getSession();
          const token = session?.access_token || null;

          if (token) {
            const resp = await fetch(`${API_URL}/api/projects/${project.id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (resp.ok) {
              const data = await resp.json();
              specToUse = data.project?.spec ?? null;
            }
          }
        } catch {
          // If fetching fails, proceed with null and the generator will handle gracefully
        }
      }

      const generatedContent = await generateInstructions({
        spec: specToUse,
        projectName: project.name
      });
      
      await createEnablement(generatedContent);
      setShowEditor(true);
    } catch (error) {
      console.error('Failed to generate instructions:', error);
      // In production, show error toast
    }
  }, [project, generateInstructions, createEnablement]);

  const handleSave = useCallback(async (content: string) => {
    await autoSave(content);
  }, [autoSave]);

  const handlePublish = useCallback(async () => {
    try {
      console.log('HandlePublish called in EnablementTab');
      await publishEnablement();
      // In production, show success toast
      console.log('Published successfully!');
    } catch (error) {
      console.error('Failed to publish in EnablementTab:', error);
      throw error; // Re-throw to be caught by Editor
    }
  }, [publishEnablement]);

  const handleBackToEmpty = useCallback(() => {
    setShowEditor(false);
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-[600px] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-3 border-zinc-200 rounded-full animate-spin mx-auto mb-4" style={{ borderTopColor: 'var(--accent)' }}></div>
          <p className="text-slate-600">Loading instructions...</p>
        </div>
      </div>
    );
  }

  // Show editor if enablement exists or user generated content
  if (enablement || showEditor) {
    return (
      <Editor
        initialContent={enablement?.instructions.content || ''}
        onSave={handleSave}
        onPublish={handlePublish}
        onBack={handleBackToEmpty}
        status={enablement?.status || 'draft'}
        lastSaved={lastSaved}
        isSaving={saving}
      />
    );
  }

  // Show empty state
  return (
    <>
      <EmptyState
        onGenerate={handleGenerate}
        isGenerating={isGenerating}
      />
      <MockNotification useMock={useMock} />
    </>
  );
};